import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Plus, Camera, Sparkles, ArrowRight, Pencil, Trash2, X, History, RotateCcw, ChevronRight, Loader2 } from "lucide-react";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { ModuleHeader } from "@/components/library/layout";
import { StatsGrid } from "@/components/library/stats";
import { EntityList, EntityDrawer } from "@/components/library/entity";
import { IncidentCard, ManualCard, AIAnalysisCard } from "@/components/library/cards";
import { Checklist } from "@/components/library/checklist";
import { CommentThread, AttachmentGallery, AuditTimeline } from "@/components/library/collab";
import { StatusBadge, PriorityBadge, Badge, relTime } from "@/components/library/badges";
import { EmptyState } from "@/components/library/states";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useStore, departmentBySlug, list, create, update, remove, addComment, addAttachment,
  saveChecklistRun, todayRun, restoreManualVersion, KNOWLEDGE_SLUGS, activityFeed,
} from "@/lib/dataStore";
import { LagerBestallning, LagerProdukter } from "@/components/lager/LagerPanels";
import { ConfirmDelete } from "@/components/shared/ConfirmDelete";
import { supabase } from "@/lib/supabaseClient";

const TABS = [
  { key: "oversikt", label: "Översikt", icon: "LayoutGrid" },
  { key: "checklistor", label: "Checklistor", icon: "ClipboardList" },
  { key: "uppgifter", label: "Uppgifter", icon: "CheckSquare" },
  { key: "problem", label: "Problem", icon: "AlertTriangle" },
  { key: "manualer", label: "Manualer", icon: "BookOpen" },
  { key: "historik", label: "Historik", icon: "History" },
];

const KNOWLEDGE_TABS = [
  { key: "manualer", label: "Manualer & policys", icon: "BookOpen" },
  { key: "historik", label: "Historik", icon: "History" },
];
const MANUAL_CATEGORIES = ["Policy", "Rutin", "Säkerhet", "Utbildning", "Övrigt"];
const FREQUENCIES = ["Dagligen", "Per skift", "Veckovis", "Månadsvis"];
const newId = () => Math.random().toString(36).slice(2, 10);

const cannedAI = () => ({
  model: "anthropic/claude-sonnet-4-6 (mock)", prompt_version: "1.0.0", confidence: 0.82, mode: "manual",
  created_at: new Date().toISOString(),
  result: { analysis: "Baserat på posten finns en återkommande risk. Åtgärda omgående och dokumentera i historiken.", risk: "medel", recommended_actions: ["Tilldela ansvarig", "Sätt deadline idag", "Bifoga foto efter åtgärd"] },
});

export default function Department() {
  useStore();
  const { slug } = useParams();
  const nav = useNavigate();
  const dept = departmentBySlug(slug);
  const { currentRestaurant, profile, hasPermission, role, currentMembership } = useSbAuth();
  const rid = currentRestaurant?.id;
  const actor = profile?.full_name || "Användare";
  const isKnowledge = KNOWLEDGE_SLUGS.includes(slug);
  const isLager = slug === "lager";
  const canManageProducts = hasPermission("foodcost.access");
  const lagerTabs = [
    { key: "bestallning", label: "Beställning", icon: "Truck" },
    ...(canManageProducts ? [{ key: "produkter", label: "Produkter", icon: "Package" }] : []),
    { key: "uppgifter", label: "Uppgifter", icon: "CheckSquare" },
    { key: "historik", label: "Historik", icon: "History" },
  ];
  const tabs = isKnowledge ? KNOWLEDGE_TABS : isLager ? lagerTabs : TABS;
  const location = useLocation();
  const defaultTab = isKnowledge ? "manualer" : isLager ? "bestallning" : "oversikt";
  const [tab, setTab] = useState(location.state?.tab || defaultTab);
  const [drawer, setDrawer] = useState(null);
  const [ai, setAi] = useState(null);
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    const d = KNOWLEDGE_SLUGS.includes(slug) ? "manualer" : slug === "lager" ? "bestallning" : "oversikt";
    setTab(location.state?.tab || d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, location.key]);

  useEffect(() => {
    const openId = location.state?.openId;
    if (!openId || !rid) return;
    const found = [...list(rid, "tasks"), ...list(rid, "incidents")].find((x) => x.id === openId);
    if (found) setDrawer(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, rid]);

  if (!dept) return <div className="p-8 text-muted-foreground">Avdelningen hittades inte.</div>;

  // Department scoping: users bound to a department (Personal) may only view their
  // own department (Knowledge is open to all). Blocks direct-URL access to others.
  const myDept = currentMembership?.department;
  if (myDept && !isKnowledge && slug !== myDept) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground" data-testid="dept-permission-denied">
        Du har inte behörighet till den här avdelningen.
      </div>
    );
  }

  const checklists = list(rid, "checklists", { department: slug });
  const tasks = list(rid, "tasks", { department: slug });
  const activeTasks = tasks.filter((t) => t.status !== "done");
  const doneTasks = tasks.filter((t) => t.status === "done");
  const incidents = list(rid, "incidents", { department: slug });
  const manuals = isKnowledge ? list(rid, "manuals") : list(rid, "manuals", { department: slug });
  const audit = activityFeed(rid, slug, 20);

  const open = incidents.filter((i) => i.status !== "resolved" && i.status !== "closed").length;
  const kpis = [
    { label: "Checklistor", value: checklists.length, testid: "dept-kpi-checklistor", onClick: () => setTab("checklistor") },
    { label: "Uppgifter", value: activeTasks.length, testid: "dept-kpi-uppgifter", onClick: () => setTab("uppgifter") },
    { label: "Öppna problem", value: open, tone: open ? "danger" : undefined, testid: "dept-kpi-problem", onClick: () => setTab("problem") },
    { label: "Manualer", value: manuals.length, testid: "dept-kpi-manualer", onClick: () => setTab("manualer") },
  ];

  return (
    <div className="max-w-[1100px] mx-auto animate-fade-up" data-testid={`department-${slug}`}>
      <div className="px-6 sm:px-10 pt-8">
        <button onClick={() => nav("/konto/operation")} className="text-sm text-muted-foreground hover:text-foreground mb-4">← Operation</button>
        <ModuleHeader department={dept} tabs={tabs} active={tab} onTab={setTab} />
      </div>

      <div className="px-6 sm:px-10 py-8">
        {tab === "bestallning" && <LagerBestallning rid={rid} actor={actor} />}
        {tab === "produkter" && <LagerProdukter rid={rid} actor={actor} canManage={canManageProducts} />}
        {tab === "oversikt" && (
          <div className="space-y-8">
            <StatsGrid items={kpis} />
            <div>
              <p className="overline text-muted-foreground mb-4">Senaste aktivitet</p>
              <AuditTimeline events={audit} />
            </div>
          </div>
        )}

        {tab === "checklistor" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="overline text-muted-foreground">{checklists.length} checklistor</p>
              {hasPermission("checklist.create") && <ChecklistEditor rid={rid} slug={slug} actor={actor} />}
            </div>
            {checklists.length === 0 ? <EmptyState title="Inga checklistor" hint={hasPermission("checklist.create") ? "Skapa den första med knappen ovan." : undefined} /> : (
              <div className="grid md:grid-cols-2 gap-4">
                {checklists.map((cl) => (
                  <ChecklistRunner key={cl.id} cl={cl} rid={rid} slug={slug} actor={actor}
                    canEdit={hasPermission("checklist.update")} canDelete={hasPermission("checklist.delete")} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "uppgifter" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="overline text-muted-foreground">{activeTasks.length} aktuella uppgifter</p>
              {hasPermission("task.create") && <TaskCreate rid={rid} slug={slug} actor={actor} />}
            </div>
            <EntityList entities={activeTasks} onOpen={setDrawer} columns={3} emptyTitle="Inga aktuella uppgifter" />
            {doneTasks.length > 0 && (
              <div className="mt-8">
                <button onClick={() => setShowDone((v) => !v)} data-testid="toggle-done-tasks"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                  <ChevronRight className={`h-4 w-4 transition-transform ${showDone ? "rotate-90" : ""}`} />
                  Klara ({doneTasks.length})
                </button>
                {showDone && <div className="mt-4 opacity-70" data-testid="done-tasks"><EntityList entities={doneTasks} onOpen={setDrawer} columns={3} emptyTitle="" /></div>}
              </div>
            )}
          </div>
        )}

        {tab === "problem" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="overline text-muted-foreground">{open} öppna · {incidents.length} totalt</p>
              <IncidentCreate rid={rid} slug={slug} actor={actor} />
            </div>
            {incidents.length === 0 ? <EmptyState title="Inga problem" hint="Allt rullar på." /> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {incidents.map((i) => <IncidentCard key={i.id} incident={i} onOpen={setDrawer} />)}
              </div>
            )}
          </div>
        )}

        {tab === "manualer" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="overline text-muted-foreground">{manuals.length} dokument</p>
              {hasPermission("manual.create") && <ManualEditor rid={rid} slug={slug} actor={actor} />}
            </div>
            {manuals.length === 0 ? <EmptyState title="Inga dokument" hint={hasPermission("manual.create") ? "Skapa en manual eller policy med knappen ovan." : undefined} /> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {manuals.map((m) => (
                  <div key={m.id} className="flex flex-col gap-2">
                    <ManualCard manual={m} onOpen={setDrawer} />
                    <div className="flex items-center gap-3 px-1 flex-wrap">
                      <ManualVersions rid={rid} manual={m} actor={actor} canRestore={hasPermission("manual.update")} />
                      {hasPermission("manual.update") && <ManualEditor rid={rid} slug={slug} actor={actor} existing={m} />}
                      {hasPermission("manual.delete") && (
                        <ConfirmDelete
                          title="Ta bort dokument?"
                          description={`"${m.title}" tas bort. Detta går inte att ångra.`}
                          onConfirm={() => { remove(rid, "manuals", m.id, actor); toast.success("Dokument borttaget"); }}
                          trigger={
                            <button data-testid={`manual-delete-${m.id}`}
                              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[hsl(0_80%_72%)] transition-colors">
                              <Trash2 className="h-3.5 w-3.5" /> Ta bort
                            </button>
                          }
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "historik" && <AuditTimeline events={activityFeed(rid, slug, 200)} />}
      </div>

      <EntityDrawer
        open={!!drawer} entity={drawer} role={role} onClose={() => { setDrawer(null); setAi(null); }}
        onDelete={(e) => { remove(rid, collectionFor(e), e.id, actor); toast.success("Borttaget"); setDrawer(null); }}
        detail={drawer && <DrawerDetail entity={drawer} rid={rid} actor={actor} onChange={(u) => setDrawer({ ...drawer, ...u })} />}
        comments={drawer && <CommentThread comments={drawer.comments || []} canComment={hasPermission("comment.create")}
          onSubmit={(b) => { addComment(rid, collectionFor(drawer), drawer.id, b, actor); setDrawer({ ...drawer, comments: [...(drawer.comments || []), { id: Date.now() + "", author: actor, body: b, created_at: new Date().toISOString() }] }); }} />}
        attachments={drawer && <AttachmentGallery attachments={drawer.attachments || []} canAdd={hasPermission("attachment.create")}
          onAdd={() => { const att = { file_name: `foto-${(drawer.attachments?.length || 0) + 1}.jpg`, mime_type: "image/jpeg", ai_tags: ["auto-tagg", dept.slug] }; addAttachment(rid, collectionFor(drawer), drawer.id, att, actor); setDrawer({ ...drawer, attachments: [...(drawer.attachments || []), { id: Date.now() + "", ...att }] }); toast.success("Bild uppladdad (mock)"); }} />}
        ai={drawer && (ai ? <AIAnalysisCard analysis={ai} /> : (
          <div className="text-center py-6">
            <Button onClick={() => setAi(cannedAI())} className="bg-accent text-white hover:opacity-90" data-testid="drawer-analyze"><Sparkles className="h-4 w-4 mr-1" />Analysera med AI</Button>
            <p className="text-xs text-muted-foreground mt-3">Genererar en AI-analys (mock tills riktig nyckel kopplas in).</p>
          </div>
        ))}
        audit={drawer && <AuditTimeline events={list(rid, "audit").filter((a) => a.entity_type === (drawer.entity_type)).slice(0, 10)} />}
      />
    </div>
  );
}

const collectionFor = (e) => ({ task: "tasks", incident: "incidents", manual: "manuals", checklist: "checklists" }[e.entity_type] || "tasks");

function DrawerDetail({ entity, rid, actor, onChange }) {
  const cfg = entity.entity_type;
  const statuses = cfg === "incident"
    ? [["open", "Öppen"], ["investigating", "Utreds"], ["resolved", "Åtgärdad"], ["closed", "Stängd"]]
    : cfg === "task" ? [["todo", "Att göra"], ["in_progress", "Pågår"], ["blocked", "Blockerad"], ["done", "Klar"]] : null;
  const setStatus = (v) => { update(rid, collectionFor(entity), entity.id, { status: v }, actor); onChange({ status: v }); };
  return (
    <div className="space-y-4 text-sm">
      <p className="text-muted-foreground whitespace-pre-wrap">{entity.description || entity.content || "Ingen beskrivning."}</p>
      {entity.assignee && <p><span className="text-muted-foreground">Ansvarig:</span> {entity.assignee}</p>}
      {entity.reporter && <p><span className="text-muted-foreground">Rapporterad av:</span> {entity.reporter}</p>}
      {statuses && (
        <div className="space-y-1.5">
          <Label>Status</Label>
          <Select value={entity.status} onValueChange={setStatus}>
            <SelectTrigger data-testid="drawer-status"><SelectValue /></SelectTrigger>
            <SelectContent>{statuses.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {cfg === "incident" && (
        <Button variant="secondary" size="sm" data-testid="incident-to-task"
          onClick={() => { create(rid, "tasks", { entity_type: "task", department: entity.department, title: `Åtgärda: ${entity.title}`, description: entity.description, status: "todo", priority: entity.severity, assignee: "", tags: ["från-problem"] }, actor); toast.success("Uppgift skapad från problem"); }}>
          Skapa uppgift av problem <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      )}
    </div>
  );
}

function ChecklistRunner({ cl, rid, slug, actor, canEdit, canDelete }) {
  const run = todayRun(rid, cl.id);
  const initial = run?.checked || [];
  const [checked, setChecked] = useState(initial);
  const items = cl.items.map((i) => ({ ...i, checked: checked.includes(i.id) }));
  return (
    <div data-testid={`checklist-card-${cl.id}`}>
      <Checklist checklist={{ ...cl, items }} onChange={setChecked} />
      <div className="flex items-center gap-3 mt-3">
        <Button size="sm" className="bg-primary text-primary-foreground hover:opacity-90" data-testid={`checklist-save-${cl.id}`}
          onClick={() => { saveChecklistRun(rid, cl.id, checked, actor); toast.success("Checklista sparad"); }}>Spara</Button>
        {canEdit && <ChecklistEditor rid={rid} slug={slug} actor={actor} existing={cl} />}
        {canDelete && (
          <ConfirmDelete
            title="Ta bort checklista?"
            description={`"${cl.title}" tas bort. Detta går inte att ångra.`}
            onConfirm={() => { remove(rid, "checklists", cl.id, actor); toast.success("Checklista borttagen"); }}
            trigger={
              <button data-testid={`checklist-delete-${cl.id}`}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[hsl(0_80%_72%)] transition-colors ml-auto">
                <Trash2 className="h-3.5 w-3.5" /> Ta bort
              </button>
            }
          />
        )}
      </div>
    </div>
  );
}

function TaskCreate({ rid, slug, actor }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", priority: "medium", assignee: "" });
  const save = () => { if (!f.title.trim()) return toast.error("Ange titel"); create(rid, "tasks", { entity_type: "task", department: slug, status: "todo", tags: [], ...f }, actor); toast.success("Uppgift skapad"); setOpen(false); setF({ title: "", description: "", priority: "medium", assignee: "" }); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-primary text-primary-foreground hover:opacity-90" data-testid="new-task"><Plus className="h-4 w-4 mr-1" />Ny uppgift</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Ny uppgift</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Titel</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title" /></div>
          <div className="space-y-1.5"><Label>Beskrivning</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Ansvarig</Label><Input value={f.assignee} onChange={(e) => setF({ ...f, assignee: e.target.value })} placeholder="Namn" data-testid="task-assignee" /></div>
            <div className="space-y-1.5"><Label>Prioritet</Label>
              <Select value={f.priority} onValueChange={(v) => setF({ ...f, priority: v })}><SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{[["high", "Hög"], ["medium", "Medel"], ["low", "Låg"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="save-task">Skapa</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncidentCreate({ rid, slug, actor }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", severity: "medium" });
  const save = () => { if (!f.title.trim()) return toast.error("Ange titel"); create(rid, "incidents", { entity_type: "incident", department: slug, status: "open", reporter: actor, tags: [], ...f }, actor); toast.success("Problem rapporterat"); setOpen(false); setF({ title: "", description: "", severity: "medium" }); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-accent text-white hover:opacity-90" data-testid="new-incident"><Plus className="h-4 w-4 mr-1" />Rapportera problem</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Rapportera problem</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Vad har hänt?</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="incident-title" /></div>
          <div className="space-y-1.5"><Label>Detaljer</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="incident-desc" /></div>
          <div className="space-y-1.5"><Label>Allvarlighet</Label>
            <Select value={f.severity} onValueChange={(v) => setF({ ...f, severity: v })}><SelectTrigger data-testid="incident-severity"><SelectValue /></SelectTrigger>
              <SelectContent>{[["high", "Hög"], ["medium", "Medel"], ["low", "Låg"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-accent text-white hover:opacity-90" data-testid="save-incident">Rapportera</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChecklistEditor({ rid, slug, actor, existing }) {
  const [open, setOpen] = useState(false);
  const seed = () => existing
    ? { title: existing.title, frequency: existing.frequency || "Dagligen", items: existing.items.map((i) => ({ ...i })) }
    : { title: "", frequency: "Dagligen", items: [{ id: newId(), text: "", requires_photo: false }] };
  const [f, setF] = useState(seed);
  const setItem = (id, patch) => setF((s) => ({ ...s, items: s.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) }));
  const addItem = () => setF((s) => ({ ...s, items: [...s.items, { id: newId(), text: "", requires_photo: false }] }));
  const removeItem = (id) => setF((s) => ({ ...s, items: s.items.filter((it) => it.id !== id) }));
  const save = () => {
    if (!f.title.trim()) return toast.error("Ange titel");
    const items = f.items.filter((i) => i.text.trim()).map((i) => ({ id: i.id, text: i.text.trim(), requires_photo: !!i.requires_photo }));
    if (items.length === 0) return toast.error("Lägg till minst en rad");
    if (existing) { update(rid, "checklists", existing.id, { title: f.title.trim(), frequency: f.frequency, items }, actor); toast.success("Checklista uppdaterad"); }
    else { create(rid, "checklists", { entity_type: "checklist", department: slug, status: "published", frequency: f.frequency, title: f.title.trim(), items }, actor); toast.success("Checklista skapad"); }
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setF(seed()); }}>
      <DialogTrigger asChild>
        {existing
          ? <button data-testid={`checklist-edit-${existing.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"><Pencil className="h-3.5 w-3.5" /> Redigera</button>
          : <Button size="sm" className="bg-primary text-primary-foreground hover:opacity-90" data-testid="new-checklist"><Plus className="h-4 w-4 mr-1" />Ny checklista</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{existing ? "Redigera checklista" : "Ny checklista"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Titel</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="checklist-title" /></div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Frekvens</Label>
              <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}><SelectTrigger data-testid="checklist-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="space-y-2">
            <Label>Rader</Label>
            {f.items.map((it, idx) => (
              <div key={it.id} className="flex items-center gap-2" data-testid={`checklist-row-${idx}`}>
                <Input value={it.text} onChange={(e) => setItem(it.id, { text: e.target.value })} placeholder={`Punkt ${idx + 1}`} data-testid={`checklist-row-input-${idx}`} />
                <button type="button" onClick={() => setItem(it.id, { requires_photo: !it.requires_photo })}
                  title="Kräver foto" data-testid={`checklist-row-photo-${idx}`}
                  className={`grid place-items-center h-9 w-9 rounded-lg border transition-colors shrink-0 ${it.requires_photo ? "bg-accent border-accent text-white" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  <Camera className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => removeItem(it.id)} className="grid place-items-center h-9 w-9 rounded-lg text-muted-foreground hover:text-[hsl(0_80%_72%)] shrink-0" data-testid={`checklist-row-remove-${idx}`}><X className="h-4 w-4" /></button>
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addItem} data-testid="checklist-add-row"><Plus className="h-4 w-4 mr-1" />Lägg till rad</Button>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="save-checklist">{existing ? "Spara ändringar" : "Skapa"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualEditor({ rid, slug, actor, existing }) {
  const [open, setOpen] = useState(false);
  const seed = () => existing
    ? { title: existing.title, category: existing.category || "Rutin", content: existing.content || "" }
    : { title: "", category: "Policy", content: "" };
  const [f, setF] = useState(seed);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const generate = async () => {
    if (!aiPrompt.trim()) return toast.error("Beskriv vad AI:n ska skriva");
    const { data: sess } = await supabase.auth.getSession();
    const token = sess?.session?.access_token;
    if (!token) return toast.error("Din session har gått ut – logga in igen");
    setAiLoading(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/generate-manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: aiPrompt.trim(), department: slug, existing_content: f.content || undefined, existing_title: f.title || undefined }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "AI-generering misslyckades");
      setF((prev) => ({
        title: prev.title?.trim() ? prev.title : (data.title || ""),
        category: data.category || prev.category,
        content: data.content || prev.content,
      }));
      toast.success("AI-förslag klart – granska och spara");
    } catch (e) {
      toast.error(e.message || "Kunde inte generera");
    } finally {
      setAiLoading(false);
    }
  };

  const save = () => {
    if (!f.title.trim()) return toast.error("Ange titel");
    if (existing) { update(rid, "manuals", existing.id, { title: f.title.trim(), category: f.category, content: f.content }, actor); toast.success("Dokument uppdaterat"); }
    else { create(rid, "manuals", { entity_type: "manual", department: slug, status: "published", author: actor, title: f.title.trim(), category: f.category, content: f.content }, actor); toast.success("Dokument skapat"); }
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setF(seed()); setAiPrompt(""); } }}>
      <DialogTrigger asChild>
        {existing
          ? <button data-testid={`manual-edit-${existing.id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"><Pencil className="h-3.5 w-3.5" /> Redigera</button>
          : <Button size="sm" className="bg-primary text-primary-foreground hover:opacity-90" data-testid="new-manual"><Plus className="h-4 w-4 mr-1" />Nytt dokument</Button>}
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{existing ? "Redigera dokument" : "Nytt dokument"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-accent/40 bg-accent/5 p-3.5 space-y-2.5" data-testid="manual-ai-box">
            <div className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-accent" />Skriv med AI</div>
            <Textarea rows={3} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={existing ? "Hur vill du ändra dokumentet? T.ex. 'lägg till ett steg om temperaturkontroll'" : "Beskriv rutinen, t.ex. 'skriv en öppningsrutin för baren'"}
              data-testid="manual-ai-prompt" />
            <Button size="sm" variant="secondary" onClick={generate} disabled={aiLoading} data-testid="manual-ai-generate" className="w-full">
              {aiLoading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Genererar…</> : <><Sparkles className="h-4 w-4 mr-1.5" />{existing ? "Uppdatera med AI" : "Generera förslag"}</>}
            </Button>
          </div>
          <div className="space-y-1.5"><Label>Titel</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="manual-title" /></div>
          <div className="space-y-1.5"><Label>Kategori</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="manual-category"><SelectValue /></SelectTrigger>
              <SelectContent>{MANUAL_CATEGORIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Innehåll</Label><Textarea rows={7} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="manual-content" /></div>
        </div>
        <DialogFooter><Button onClick={save} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="save-manual">{existing ? "Spara ändringar" : "Skapa"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualVersions({ rid, manual, actor, canRestore }) {
  const [open, setOpen] = useState(false);
  const versions = manual.versions || [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`manual-versions-${manual.id}`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <History className="h-3.5 w-3.5" /> Versioner{versions.length ? ` (${versions.length})` : ""}
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Versionshistorik — {manual.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl border border-accent/40 bg-card p-4" data-testid="manual-version-current">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="font-display font-semibold text-sm truncate">{manual.title}</span>
              <Badge tone="accent">Nuvarande</Badge>
            </div>
            <p className="text-xs text-muted-foreground font-mono mb-2">{manual.category} · uppdaterad {relTime(manual.updated_at)}</p>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-4">{manual.content}</p>
          </div>
          {versions.length === 0 && (
            <p className="text-sm text-muted-foreground">Inga tidigare versioner ännu. Varje gång dokumentet ändras sparas den föregående versionen här.</p>
          )}
          {versions.map((v) => (
            <div key={v.id} className="rounded-xl border border-border bg-card p-4" data-testid={`manual-version-${v.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-display font-semibold text-sm truncate">{v.title}</span>
                <Badge>v{v.version}</Badge>
              </div>
              <p className="text-xs text-muted-foreground font-mono mb-2">{v.category} · sparad av {v.saved_by} · {relTime(v.saved_at)}</p>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-4">{v.content}</p>
              {canRestore && (
                <Button size="sm" variant="secondary" className="mt-3" data-testid={`manual-restore-${v.id}`}
                  onClick={() => { restoreManualVersion(rid, manual.id, v.id, actor); toast.success(`Version ${v.version} återställd`); setOpen(false); }}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Återställ denna version
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
